import React, { FunctionComponent, useMemo, useState } from "react";

import { Pivot, PivotItem, Separator, Stack, Toggle } from "@bps/fluent-ui";
import { PltUser } from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  useUserPermissions,
  useUserRoles
} from "@libs/api/gateways/plt/plt-gateway.hooks";

import { PermissionsAndRolesItem } from "./PermissionsAndRolesItem";

enum SelectedKeys {
  permissions = "permissions",
  roles = "roles",
  businessRoles = "businessRoles"
}

interface PermissionsAndRolesProps {
  user: PltUser;
}

export const PermissionsAndRoles: FunctionComponent<PermissionsAndRolesProps> = ({
  user
}) => {
  const [selectedKey, setSelectedKey] = useState<SelectedKeys>(
    SelectedKeys.permissions
  );

  const [showNewSecModel, setShowNewSecModel] = useState<boolean>(false);

  const { data: newSecUserRoles } = useUserRoles(user.tenantId, user.id);
  const { data: newSecUserPermissions = [] } = useUserPermissions(
    user.tenantId,
    user.id
  );

  const { permissions, businessRoles, roles } = useMemo(() => {
    if (showNewSecModel)
      return {
        permissions: newSecUserPermissions?.map(p => p.permissionCode),
        roles: (newSecUserRoles?.securityRoles ?? [])
          ?.filter(r => r.isGranted)
          .map(r => r.securityRoleCode),
        businessRoles: newSecUserRoles?.businessRoles ?? []
      };

    return {
      permissions: user?.permissions ?? [],
      roles: (user?.roles ?? []).map(r => r.code),
      businessRoles: user?.businessRoles ?? []
    };
  }, [
    newSecUserPermissions,
    newSecUserRoles?.businessRoles,
    newSecUserRoles?.securityRoles,
    showNewSecModel,
    user?.businessRoles,
    user?.permissions,
    user?.roles
  ]);

  return (
    <Stack
      horizontal
      styles={{ root: { width: "100%" } }}
      tokens={{ childrenGap: 8 }}
    >
      {/* Show this toggle temporary to show new Sec Model data  */}
      <Toggle
        label="New security model"
        checked={showNewSecModel}
        onChange={() => setShowNewSecModel(prev => !prev)}
        styles={{ root: { margin: 0, width: 160 } }}
      />

      <Separator vertical />
      <Pivot
        selectedKey={selectedKey}
        onLinkClick={item =>
          item?.props.itemKey &&
          setSelectedKey(item.props.itemKey as SelectedKeys)
        }
        style={{ width: "100%" }}
        styles={{ root: { paddingBottom: 16 } }}
      >
        <PivotItem itemKey={SelectedKeys.permissions} headerText="Permissions">
          <PermissionsAndRolesItem items={permissions} />
        </PivotItem>
        <PivotItem headerText="Roles" itemKey={SelectedKeys.roles}>
          <PermissionsAndRolesItem items={roles} />
        </PivotItem>
        <PivotItem
          headerText="Business roles"
          itemKey={SelectedKeys.businessRoles}
        >
          <PermissionsAndRolesItem items={businessRoles} />
        </PivotItem>
      </Pivot>
    </Stack>
  );
};
